import React, { useState, useEffect } from 'react';
import { FaFacebook, FaInstagram, FaPhone } from 'react-icons/fa';
import Navigation from '../../components/Navigation';
import { getProducts, getTestimonials, getFragrances, getBusinessSettings } from '../../api/publicApi';

function HomePage() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [fragrances, setFragrances] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, testimonialsData, fragrancesData, settingsData] = await Promise.all([
        getProducts(),
        getTestimonials(),
        getFragrances(),
        getBusinessSettings()
      ]);

      setProducts(productsData);
      setTestimonials(testimonialsData);
      setFragrances(fragrancesData);
      setSettings(settingsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Group products by theme
  const productsByTheme = products.reduce((acc, product) => {
    const themeKey = product.theme_slug || 'other';
    if (!acc[themeKey]) {
      acc[themeKey] = {
        name: product.theme_name,
        slug: product.theme_slug,
        products: []
      };
    }
    acc[themeKey].products.push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="App">
        <header className="header" role="banner">
          <Navigation />
        </header>
        <main style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <p>Chargement...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <header className="header" role="banner">
          <Navigation />
        </header>
        <main style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <p style={{ color: 'red' }}>{error}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="header" role="banner">
        <Navigation />
      </header>

      <main role="main">

        <section id="about" className="about" aria-labelledby="about-title">
          <h2 id="about-title">{settings?.company_name || 'Cire Étoilée'}</h2>
          <p style={{ whiteSpace: 'pre-line' }}>{settings?.about_text || 'Je crée avec passion des bougies artisanales aux parfums délicats pour illuminer vos moments précieux.'}</p>
        </section>

        <section id="products" className="products" aria-labelledby="products-title">
          {Object.values(productsByTheme).map((theme, index) => (
            <React.Fragment key={theme.slug || index}>
              <h2 id="products-title">{theme.name}</h2>
              <div className="product-grid" role="list">
                {theme.products.map(product => (
                  <article
                    key={product.id}
                    className="product-card"
                    role="listitem"
                    style={{
                      opacity: product.is_visible ? 1 : 0.5,
                      filter: product.is_visible ? 'none' : 'grayscale(0.5)',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: product.is_visible ? 'rgba(39, 174, 96, 0.9)' : 'rgba(231, 76, 60, 0.9)',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      {product.is_visible ? 'Disponible' : 'Indisponible'}
                    </div>
                    <div className="product-image">
                      <img src={product.image_path} alt={`Bougie ${product.name}`} loading="lazy" />
                    </div>
                  </article>
                ))}
              </div>
            </React.Fragment>
          ))}
        </section>

        <section id="fragrance" className="fragrance" aria-labelledby="fragrance-title">
          <h2 id="fragrance-title">Nos Senteurs</h2>
          <p>{settings?.fragrance_description || 'Fragrances de Grasse qui respectent les normes Européennes et françaises ( IFRA )'}</p>
          <div className="perfume-section">
            {fragrances.map((category) => (
              <div key={category.id} className="perfume-category">
                <h2 style={{ color: category.color, textAlign: "left" }}>
                  {category.name}
                </h2>
                <ul className="perfume-list" style={{ color: category.color, margin: 0, paddingLeft: 18 }}>
                  {category.fragrances && category.fragrances.map((fragrance) => (
                    <li
                      key={fragrance.id}
                      className={!fragrance.is_available ? 'unavailable' : ''}
                      style={{
                        opacity: fragrance.is_available ? 1 : 0.5,
                        textDecoration: fragrance.is_available ? 'none' : 'line-through',
                        color: fragrance.is_available ? 'inherit' : '#999'
                      }}
                    >
                      {fragrance.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section id="testimonials" className="testimonials" aria-labelledby="testimonials-title">
          <h2 id="testimonials-title">Avis Clients</h2>
          <div className="testimonials-grid" role="list">
            {testimonials.map(testimonial => (
              <article key={testimonial.id} className="testimonial-card" role="listitem">
                <div className="testimonial-content">
                  <div className="testimonial-rating" aria-label={`Note: ${testimonial.rating} sur 5`}>
                    {"★".repeat(testimonial.rating)}
                  </div>
                  <p className="testimonial-text">{testimonial.content}</p>
                  <div className="testimonial-author">
                    <strong>{testimonial.author_name}</strong>
                    <span className="testimonial-date">{testimonial.review_date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="contact" className="contact" aria-labelledby="contact-title">
          <h2 id="contact-title">Contactez-nous</h2>
          <div className="contact-info">
            <div className="social-links">
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Suivez-nous sur Facebook"
                >
                  <FaFacebook />
                  <span>Facebook</span>
                </a>
              )}
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Suivez-nous sur Instagram"
                >
                  <FaInstagram />
                  <span>Instagram</span>
                </a>
              )}
              {settings?.phone && (
                <a
                  href={`tel:${settings.phone.replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Appelez-nous"
                >
                  <FaPhone />
                  <span>{settings.phone}</span>
                </a>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="footer" role="contentinfo">
        <p>&copy; {new Date().getFullYear()} {settings?.company_name || 'Cire Étoilée'}. {settings?.footer_text || 'Tous droits réservés'}.</p>
      </footer>
    </div>
  );
}

export default HomePage;
